import {
	IAppWillLoadPageHandler,
	ILogger,
	IPageWillMountHandler,
	LifeCycle,
	LoggerSymbol,
} from '@wix/thunderbolt-symbols'
import { multi, withDependencies } from '@wix/thunderbolt-ioc'
import { taskify } from '@wix/thunderbolt-commons'
import { INavigationManager, NavigationManagerSymbol } from 'feature-navigation-manager'
import { InitPagePhasesSymbol, PageProviderSymbol } from './symbols'
import type { IInitPagePhases, IPageInitializer, IPageProvider } from './types'

export const PageInitializer = withDependencies(
	[
		multi(LifeCycle.AppWillLoadPageHandler),
		PageProviderSymbol,
		LoggerSymbol,
		NavigationManagerSymbol,
		InitPagePhasesSymbol,
	],
	(
		appWillLoadPageHandlers: Array<IAppWillLoadPageHandler>,
		pageProvider: IPageProvider,
		logger: ILogger,
		navigationManager: INavigationManager,
		initPagePhases: IInitPagePhases
	): IPageInitializer => ({
		initPage: async ({ pageId, contextId }) => {
			logger.phaseStarted('init_page')
			initPagePhases.clear()
			const pageReflectorPromise = pageProvider(contextId, pageId)
			const pageWillMount = taskify(async () => {
				const pageReflector = await pageReflectorPromise
				const pageWillMountHandlers = pageReflector.getAllImplementersOf<IPageWillMountHandler>(
					LifeCycle.PageWillMountHandler
				)

				// TB-4458 upon navigation, we want to run all lifecycles that may change props synchronously so that we don't re-render components with partial props
				// TODO things work by chance. we should probably block react rendering during navigation.

				const pageWillMountWithReporting = async (handler: IPageWillMountHandler) => {
					const phaseEnd = initPagePhases.start(`pageWillMount_${handler.name}`)
					await handler.pageWillMount(pageId)
					phaseEnd()
				}

				await Promise.all(
					navigationManager.isFirstNavigation()
						? pageWillMountHandlers.map((handler) => taskify(() => pageWillMountWithReporting(handler)))
						: pageWillMountHandlers.map(pageWillMountWithReporting)
				)
			})

			await Promise.all([
				pageWillMount,
				...appWillLoadPageHandlers.map((handler) =>
					taskify(() => {
						const phaseEnd = initPagePhases.start(`appWillLoadPage_${handler.name}`)
						const promiseOrVoid = handler.appWillLoadPage({ pageId, contextId })
						if (promiseOrVoid) {
							return promiseOrVoid.then(phaseEnd)
						} else {
							phaseEnd()
						}
					})
				),
			])
			logger.addBreadcrumbToBatch('init_page_phase_durations', initPagePhases.getDurations())
			logger.phaseEnded('init_page')
		},
	})
)
